import CreditCardIcon from '@mui/icons-material/CreditCard';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import { analyticsService } from 'services/analytics/analyticsService';

// import PaymentMethodTabs, {
//   type PaymentMethodTabValue,
// } from 'components/PaymentMethodTabs';
// import SubscriptionPixTab from './SubscriptionPixTab';
import styles from './styles';
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';
import {
  createSubscriptionWithCard,
  createSubscriptionPlan,
} from 'services/paymentService';
import { updateSubscriptionById } from 'services/firebase/UpdateUserSubscription';
import { applyValidatedSubscriptionCodeToUser } from 'services/firebase/applySubscriptionCode';
import {
  normalizeSubscriptionCode,
  type SubscriptionCodeValidationResult,
  validateSubscriptionCode,
} from 'services/firebase/subscriptionCodeService';
import { buildSubscriptionPaymentDetails } from 'utils/subscriptionPayment';
import {
  formatCurrencyBRL,
  parsePlanPrice,
  roundCurrency,
  sumCurrency,
} from 'utils/currency';
import { auth } from 'services/firebase/client';
import { logPaymentError } from 'services/firebase/errorLogService';
import ErrorModal from 'components/ErrorModal';

// Tipo do erro do Mercado Pago Brick
interface IBrickError {
  type: 'non_critical' | 'critical';
  cause: string;
  message: string;
}

initMercadoPago('APP_USR-a95da5cf-9b11-4dba-b56d-061a1e0be09f');

const StableCardPayment = React.memo(CardPayment);

// const YEAR_2025 = 2025;

const SubscriptionPayment = () => {
  const navigate = useNavigate();

  const {
    selectedPlan,
    shippingOption,
    personalData,
    usedReferralCode,
    setUsedReferralCode,
  } = useSubscription();
  const [isProcessing] = useState(false);
  // const [paymentTab, setPaymentTab] = useState<PaymentMethodTabValue>('card');
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [referralCodeError, setReferralCodeError] = useState('');
  const [referralCodeSuccess, setReferralCodeSuccess] = useState('');
  const [referralCodeChecking, setReferralCodeChecking] = useState(false);
  const [validatedCode, setValidatedCode] = useState<{
    normalized: string;
    result: SubscriptionCodeValidationResult;
  } | null>(null);
  const [cardBrickBlocked, setCardBrickBlocked] = useState(false);
  const [cardBrickKey, setCardBrickKey] = useState(0);
  const lastHandledBrickErrorRef = useRef<string | null>(null);
  const usedReferralCodeRef = useRef(usedReferralCode);
  const validatedCodeRef = useRef(validatedCode);

  const updateValidatedCode = useCallback(
    (
      next: {
        normalized: string;
        result: SubscriptionCodeValidationResult;
      } | null,
    ) => {
      validatedCodeRef.current = next;
      setValidatedCode(next);
    },
    [],
  );

  useEffect(() => {
    usedReferralCodeRef.current = usedReferralCode;
  }, [usedReferralCode]);

  const validateAndStoreSubscriptionCode = useCallback(
    async (code: string) => {
      const normalized = normalizeSubscriptionCode(code);
      if (!normalized || normalized.length < 2) {
        setReferralCodeError('');
        setReferralCodeSuccess('');
        updateValidatedCode(null);
        return null;
      }

      if (validatedCodeRef.current?.normalized === normalized) {
        return validatedCodeRef.current.result;
      }

      setReferralCodeChecking(true);
      setReferralCodeError('');
      setReferralCodeSuccess('');
      try {
        const result = await validateSubscriptionCode(normalized);
        if (result) {
          if (result.type === 'referral') {
            setReferralCodeSuccess('Código de indicação válido.');
          } else {
            setReferralCodeSuccess(
              `Código promocional válido! Você recebe ${result.promo.extraBeers} cerveja(s) extra por ${result.promo.months} mês(es).`,
            );
          }
          updateValidatedCode({ normalized, result });
        } else {
          setReferralCodeError(
            'Código não encontrado. Verifique ou deixe em branco.',
          );
          updateValidatedCode(null);
        }
        return result;
      } catch {
        setReferralCodeError(
          'Não foi possível validar o código. Tente novamente.',
        );
        updateValidatedCode(null);
        return null;
      } finally {
        setReferralCodeChecking(false);
      }
    },
    [updateValidatedCode],
  );

  useEffect(() => {
    if (!selectedPlan) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate('/');
    } else {
      analyticsService.trackPageView('/payment', 'Pagamento');
      analyticsService.trackSubscriptionFlow(
        'payment',
        selectedPlan.type,
        selectedPlan.total,
      );
    }
  }, [navigate, selectedPlan]);

  // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
  //   const { name, value } = e.target;
  //   setFormData(prev => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  // const handleSelectChange = useCallback((event: SelectChangeEvent<string>) => {
  //   const { name, value } = event.target;
  //   if (name) {
  //     setFormData(prev => ({ ...prev, [name]: value }));
  //   }
  // }, []);

  const handleBack = () => {
    navigate('/shipping');
  };

  const subtotal = roundCurrency(
    selectedPlan?.subtotal ?? parsePlanPrice(selectedPlan?.price),
  );
  const shippingPrice = roundCurrency(
    shippingOption?.price ?? selectedPlan?.frete ?? 0,
  );
  const total = sumCurrency(subtotal, shippingPrice);

  // Validate total amount before allowing payment
  useEffect(() => {
    if (selectedPlan && total <= 0) {
      console.error('Invalid total amount:', total);
      window.location.href = '/error';
    }
  }, [selectedPlan, total]);

  const handleCardError = useCallback(
    async (error: IBrickError) => {
      const errorSignature = `${error.type}:${error.cause}:${error.message}`;
      if (lastHandledBrickErrorRef.current === errorSignature) {
        return;
      }
      lastHandledBrickErrorRef.current = errorSignature;

      // Mensagem amigável para o usuário
      let userMessage = 'Ocorreu um erro ao processar o pagamento. ';

      if (error.type === 'critical') {
        userMessage +=
          'Por favor, verifique os dados do cartão e tente novamente.';
      } else {
        userMessage += 'Por favor, tente novamente.';
      }

      // Mensagem mais específica baseada na causa
      if (error.cause) {
        switch (error.cause) {
          case 'invalid_card_number':
            userMessage =
              'Número do cartão inválido. Verifique e tente novamente.';
            break;
          case 'invalid_card_holder_name':
            userMessage =
              'Nome do titular inválido. Verifique e tente novamente.';
            break;
          case 'invalid_expiration_date':
            userMessage =
              'Data de validade inválida. Verifique e tente novamente.';
            break;
          case 'invalid_security_code':
            userMessage =
              'Código de segurança inválido. Verifique e tente novamente.';
            break;
          case 'insufficient_funds':
            userMessage =
              'Saldo insuficiente. Verifique o saldo do cartão e tente novamente.';
            break;
          case 'card_disabled':
            userMessage =
              'Cartão desabilitado. Entre em contato com o banco emissor.';
            break;
          case 'fields_setup_failed_after_3_tries':
            userMessage =
              'Nao foi possivel carregar o formulario de cartao agora. Tente recarregar o formulario abaixo.';
            setCardBrickBlocked(true);
            break;
          default:
            // Mantém a mensagem padrão
            break;
        }
      }

      setErrorMessage(userMessage);
      setErrorModalOpen(true);

      // Logar erro no Firebase
      await logPaymentError({
        type: error.type,
        cause: error.cause,
        message: error.message,
        context: {
          page: 'subscription_payment',
          subscriptionPlan: selectedPlan?.type,
          amount: total,
          errorCause: error.cause,
          errorType: error.type,
        },
      });

      // Logar no Analytics também
      analyticsService.trackSubscriptionFlow(
        'error',
        selectedPlan?.type,
        total,
      );

      console.error('Erro no CardPayment:', {
        type: error.type,
        cause: error.cause,
        message: error.message,
      });
    },
    [selectedPlan, total],
  );

  const handleRetryCardBrick = useCallback(() => {
    lastHandledBrickErrorRef.current = null;
    setCardBrickBlocked(false);
    setErrorModalOpen(false);
    setCardBrickKey(prev => prev + 1);
  }, []);

  const cardPaymentInitialization = useMemo(
    () => ({
      amount: total,
      payer: { email: personalData.email },
    }),
    [personalData.email, total],
  );

  const cardPaymentCustomization = useMemo(
    () => ({
      paymentMethods: {
        maxInstallments: 1,
        types: {
          included: ['credit_card' as const],
        },
      },
    }),
    [],
  );

  const handleCardSubmit = useCallback(
    async (param: any) => {
      if (!selectedPlan) {
        console.error('selectedPlan is null');
        window.location.href = '/error';
        return;
      }

      analyticsService.trackCta(
        'subscription_payment_card_submit',
        'Pagar com Cartão',
        {
          subscription_plan: selectedPlan.label,
          amount: total,
        },
      );

      try {
        const normalizedCode = normalizeSubscriptionCode(
          usedReferralCodeRef.current,
        );
        const codeResult = normalizedCode
          ? await validateAndStoreSubscriptionCode(normalizedCode)
          : null;

        if (normalizedCode && !codeResult) {
          return;
        }

        // Criar o plano dinamicamente antes de processar o pagamento
        const reason = `Clube da 42 - ${selectedPlan.label} - ${selectedPlan.type}`;
        const repetitions = 18;
        const transaction_amount = total;

        const planResponse = await createSubscriptionPlan({
          reason,
          repetitions,
          transaction_amount,
        });

        if (!planResponse || !planResponse.id) {
          console.error('Erro ao criar plano: ID não retornado');
          window.location.href = '/error';
          return;
        }

        const planId = planResponse.id;

        const response = await createSubscriptionWithCard({
          token: param.token,
          payer: {
            email: param.payer.email,
            identification: {
              type: 'CPF',
              number: param.payer.identification.number,
            },
          },
          planId: planId,
          subscriptionID: selectedPlan?.subscriptionID || '',
        });

        if (!response || response.error || response.status !== 'authorized') {
          window.location.href = '/error';
          return;
        }

        const user = auth.currentUser;
        if (!user) {
          console.error('Usuário não autenticado');
          window.location.href = '/error';
          return;
        }

        if (!selectedPlan) {
          console.error('selectedPlan is null');
          window.location.href = '/error';
          return;
        }
        const preapprovalId = String(
          response.preapproval_id ?? response.id ?? '',
        );
        await updateSubscriptionById(
          user.uid,
          selectedPlan.subscriptionID,
          preapprovalId,
          planId,
          buildSubscriptionPaymentDetails('credit_card', preapprovalId),
        );

        if (codeResult) {
          await applyValidatedSubscriptionCodeToUser(user.uid, codeResult);
        }

        analyticsService.trackPurchase(
          selectedPlan.subscriptionID,
          total,
          'BRL',
          [
            {
              item_id: selectedPlan.pack || selectedPlan.label,
              item_name: `${selectedPlan.label} - ${selectedPlan.type}`,
              category: 'subscription',
              quantity: 1,
              price: total,
            },
          ],
        );
        window.location.href = '/success';
      } catch (err) {
        console.error('Erro ao processar assinatura:', err);
        window.location.href = '/error';
      }
    },
    [selectedPlan, total, validateAndStoreSubscriptionCode],
  );

  return (
    <Box sx={{ ...styles.container, px: { xs: 2, sm: 4 } }}>
      <Grid container spacing={2}>
        {/* Left Column */}
        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
          <Typography
            variant="subtitle1"
            sx={{ ...styles.subtitle, textAlign: 'center', mb: 2 }}
          >
            FORMA DE PAGAMENTO
          </Typography>

          {/* PIX temporariamente desabilitado
          <PaymentMethodTabs value={paymentTab} onChange={setPaymentTab} />
          */}

          {/* {paymentTab === 'card' && ( */}
          <>
            <Card
              sx={{
                ...styles.card,
                mx: { xs: 1, sm: 2 },
                px: { xs: 2, sm: 3 },
              }}
            >
              <CardContent sx={styles.paymentHeader}>
                <CreditCardIcon sx={styles.icon} />
                <Typography variant="body1">
                  Pagar com Cartão de Crédito
                </Typography>
              </CardContent>
            </Card>
            {cardBrickBlocked ? (
              <Card sx={{ ...styles.card, mx: { xs: 1, sm: 2 }, p: 2 }}>
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRetryCardBrick}
                    >
                      Tentar novamente
                    </Button>
                  }
                >
                  Nao foi possivel carregar o formulario do cartao nesta
                  tentativa.
                </Alert>
              </Card>
            ) : (
              <StableCardPayment
                key={cardBrickKey}
                initialization={cardPaymentInitialization}
                onSubmit={handleCardSubmit}
                onError={handleCardError}
                locale="pt-BR"
                customization={cardPaymentCustomization}
              />
            )}
          </>
          {/* )} */}

          {/* PIX temporariamente desabilitado
          {paymentTab === 'pix' && selectedPlan && (
            <SubscriptionPixTab
              key={`${selectedPlan.subscriptionID}-${total}`}
              total={total}
              disabled={isProcessing}
            />
          )}
          */}

          {/* <Card
            sx={{ ...styles.card, mx: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}
          >
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ width: { xs: '100%', sm: '95%', md: '90%' } }}
                    label="CPF"
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleInputChange}
                    helperText=""
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ width: { xs: '100%', sm: '95%', md: '90%' } }}
                    label="Número do cartão"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    sx={{ width: { xs: '100%', sm: '95%', md: '90%' } }}
                    label="Nome do titular (Como escrito no cartão)"
                    name="cardHolder"
                    value={formData.cardHolder}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl
                    fullWidth
                    sx={{ width: { xs: '100%', sm: '95%', md: '90%' } }}
                  >
                    <InputLabel>Mês</InputLabel>
                    <Select
                      name="expirationMonth"
                      value={formData.expirationMonth}
                      onChange={handleSelectChange}
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>
                          {i + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl
                    fullWidth
                    sx={{ width: { xs: '100%', sm: '95%', md: '90%' } }}
                  >
                    <InputLabel>Ano</InputLabel>
                    <Select
                      name="expirationYear"
                      value={formData.expirationYear}
                      onChange={handleSelectChange}
                    >
                      {Array.from({ length: 10 }, (_, i) => (
                        <MenuItem key={YEAR_2025 + i} value={YEAR_2025 + i}>
                          {YEAR_2025 + i}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    sx={{ width: { xs: '100%', sm: '95%', md: '90%' } }}
                    label="Cód. de segurança"
                    name="securityCode"
                    value={formData.securityCode}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    sx={{ width: { xs: '100%', sm: '95%', md: '90%' } }}
                  >
                    <InputLabel>Parcelamento</InputLabel>
                    <Select
                      name="installment"
                      value={formData.installment}
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="À vista (R$ 40,95) sem juros">
                        À vista (R$ 40,95) sem juros
                      </MenuItem>
                      <MenuItem value="2x sem juros (R$ 20,48)">
                        2x sem juros (R$ 20,48)
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card> */}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
          <Typography variant="subtitle1" sx={styles.subtitle}>
            RESUMO
          </Typography>
          <Card sx={{ ...styles.summaryCard, mx: { xs: 1, sm: 2 } }}>
            <CardContent>
              <Typography variant="body1" sx={styles.productText}>
                {`${selectedPlan?.label} - ${selectedPlan?.type}`}
              </Typography>
              <Typography variant="body2" sx={styles.quantityText}>
                Quantidade: {selectedPlan?.quantity}
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  Código promocional ou de indicação
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <TextField
                    fullWidth
                    label="Digite seu código"
                    placeholder="Ex: Nome-ABC123"
                    value={usedReferralCode}
                    onChange={e => {
                      const nextCode = e.target.value;
                      setUsedReferralCode(nextCode);
                      if (referralCodeError) setReferralCodeError('');
                      if (referralCodeSuccess) setReferralCodeSuccess('');
                      if (
                        validatedCode &&
                        validatedCode.normalized !==
                          normalizeSubscriptionCode(nextCode)
                      ) {
                        updateValidatedCode(null);
                      }
                    }}
                    error={!!referralCodeError}
                    helperText={
                      referralCodeChecking
                        ? 'Verificando código...'
                        : referralCodeError || referralCodeSuccess || undefined
                    }
                    FormHelperTextProps={{
                      sx: {
                        ...(referralCodeSuccess && !referralCodeError
                          ? { color: 'success.main' }
                          : {}),
                      },
                    }}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      void validateAndStoreSubscriptionCode(usedReferralCode);
                    }}
                    disabled={
                      referralCodeChecking ||
                      normalizeSubscriptionCode(usedReferralCode).length < 2
                    }
                    sx={{
                      minWidth: { xs: '100%', sm: 136 },
                      height: 40,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Validar codigo
                  </Button>
                </Box>
              </Box>

              <Box sx={styles.summaryContainer}>
                <Typography variant="body2" sx={styles.summaryText}>
                  SUBTOTAL
                </Typography>
                <Typography variant="body2" sx={styles.summaryValue}>
                  R$ {formatCurrencyBRL(subtotal)}
                </Typography>
              </Box>
              <Box sx={styles.summaryContainer}>
                <Typography variant="body2" sx={styles.summaryText}>
                  FRETE
                </Typography>
                <Typography variant="body2" sx={styles.summaryValue}>
                  {shippingPrice === 0
                    ? 'GRÁTIS'
                    : `R$ ${formatCurrencyBRL(shippingPrice)}`}
                </Typography>
              </Box>
              <Box sx={styles.summaryContainer}>
                <Typography variant="body1" sx={styles.totalText}>
                  TOTAL
                </Typography>
                <Typography variant="h6" sx={styles.totalValue}>
                  R$ {formatCurrencyBRL(total)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
          <Box sx={styles.footer}>
            {/* <Button variant="text" onClick={handleBack} sx={styles.backButton}>
              Voltar
            </Button> */}
            <Button
              variant="contained"
              onClick={handleBack}
              sx={styles.advanceButton}
            >
              {isProcessing ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Voltar'
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <ErrorModal
        open={errorModalOpen}
        message={errorMessage}
        onClose={() => setErrorModalOpen(false)}
      />
    </Box>
  );
};

export default SubscriptionPayment;
